import type { ReactNode } from "react";

export type GuideItem = { q: string; plain: string; answer: ReactNode };
export type GuideGroup = { category: string; items: GuideItem[] };

function P({ children }: { children: ReactNode }) {
  return <p className="leading-relaxed">{children}</p>;
}

function Weak({ children }: { children: ReactNode }) {
  return (
    <p className="mt-3 rounded-xl border border-destructive/30 bg-destructive/5 px-3.5 py-2.5">
      ❌ <strong>Weak:</strong> {children}
    </p>
  );
}

function Strong({ children }: { children: ReactNode }) {
  return (
    <p className="mt-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-3.5 py-2.5">
      ✅ <strong>Strong:</strong> {children}
    </p>
  );
}

export const FRESHER_GROUPS: GuideGroup[] = [
  {
    category: "Category A · Mindset & 15-Second Rule",
    items: [
      {
        q: 'What is the "15-Second Rule" in an F-1 visa interview?',
        plain:
          "The clock starts the moment you speak. Your opening response sets the entire credibility lens through which the visa officer evaluates your remaining answers. Starting strong with precision, confidence, and clear intent creates momentum, whereas a weak or scripted opening puts you on the defensive.",
        answer: (
          <P>
            The clock starts the moment you speak. Your opening response sets the entire credibility
            lens through which the visa officer evaluates your remaining answers. Starting strong
            with precision, confidence, and clear intent creates momentum, whereas a weak or
            scripted opening puts you on the defensive.
          </P>
        ),
      },
      {
        q: "Why do smart students with top GRE scores still get rejected?",
        plain:
          "Visa officers don't approve test scores or high university rankings—they approve credible stories. Most smart students get refused because their answers sound scripted and predictable. When an officer senses memorized wording, confidence starts looking artificial, leading the officer to interrupt quickly and probe deeper. Focus on conversational logic rather than memorizing paragraphs.",
        answer: (
          <P>
            Visa officers don&apos;t approve test scores or high university rankings—they approve
            credible stories. Most smart students get refused because their answers sound scripted
            and predictable. When an officer senses memorized wording, confidence starts looking
            artificial, leading the officer to interrupt quickly and probe deeper. Focus on
            conversational logic rather than memorizing paragraphs.
          </P>
        ),
      },
    ],
  },
  {
    category: "Category B · Academics, Low GPA & Backlogs",
    items: [
      {
        q: "I have a low GPA or backlogs. Will my visa be rejected automatically?",
        plain:
          'No. Consular officers evaluate overall clarity, academic progression, and intent—not just your GPA. The fastest way to hurt your profile is giving defensive excuses (e.g., "My college was too hard"). Weak answer: "My GPA was low because college was difficult." Strong answer: "My academics had initial setbacks, but during my final years I became deeply focused on my specialization and built clear long-term career goals."',
        answer: (
          <>
            <P>
              No. Consular officers evaluate overall clarity, academic progression, and intent—not
              just your GPA. The fastest way to hurt your profile is giving defensive excuses (e.g.,
              &quot;My college was too hard&quot;).
            </P>
            <Weak>&quot;My GPA was low because college was difficult.&quot;</Weak>
            <Strong>
              &quot;My academics had initial setbacks, but during my final years I became deeply
              focused on my specialization and built clear long-term career goals.&quot;
            </Strong>
          </>
        ),
      },
      {
        q: "Does choosing a high-ranked university guarantee my visa approval?",
        plain:
          'No. There is no official metric known as a "Visa Acceptance Rate" for universities. A famous university cannot save a weak career plan. Officers judge academic logic: Does this specific choice make sense for your career trajectory? Specificity beats brand prestige every single time.',
        answer: (
          <P>
            No. There is no official metric known as a &quot;Visa Acceptance Rate&quot; for
            universities. A famous university cannot save a weak career plan. Officers judge
            academic logic: Does this specific choice make sense for your career trajectory?
            Specificity beats brand prestige every single time.
          </P>
        ),
      },
    ],
  },
  {
    category: "Category C · Funding, Loans & Sponsorship",
    items: [
      {
        q: "Can I show multiple financial sponsors (Dad, Mom, Grandfather, Aunt + Loan)?",
        plain:
          'Having multiple sponsors is allowed, but a messy financial structure creates suspicion and confusion. Officers favor simplicity. Recommended pitch: "My education is primarily funded through an approved education loan, supported by family savings from my parents."',
        answer: (
          <>
            <P>
              Having multiple sponsors is allowed, but a messy financial structure creates suspicion
              and confusion. Officers favor simplicity.
            </P>
            <div className="mt-3 overflow-x-auto rounded-xl border border-border">
              <table className="w-full min-w-[420px] text-left text-sm">
                <thead className="bg-muted/60 text-xs uppercase tracking-wider text-foreground/60">
                  <tr>
                    <th className="px-3 py-2 font-semibold">❌ Messy funding</th>
                    <th className="px-3 py-2 font-semibold">✅ Clean funding</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border align-top">
                  <tr>
                    <td className="px-3 py-2.5">Dad + Mom + Grandfather + Aunt + partial loan</td>
                    <td className="px-3 py-2.5">Education loan (primary) + parents&apos; savings</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2.5">4–5 bank accounts, unclear ownership</td>
                    <td className="px-3 py-2.5">1 loan sanction letter + 1–2 family accounts</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2.5">No single person accountable for the funding</td>
                    <td className="px-3 py-2.5">One clear primary sponsor you can explain in a line</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <Strong>
              &quot;My education is primarily funded through an approved education loan, supported by
              family savings from my parents.&quot;
            </Strong>
          </>
        ),
      },
      {
        q: "Can I show different funding sources at the interview than what is listed on my I-20?",
        plain:
          'Yes. Changing funds is acceptable if it follows clean logic. For example, if your education loan was approved after your I-20 was issued, simply explain: "At the time of I-20 issuance, we showed available family funds. Since then, our formal education loan has been sanctioned for additional support." Avoid adding random, unexplained third-party sponsors last minute.',
        answer: (
          <>
            <P>
              Yes. Changing funds is acceptable if it follows clean logic. For example, if your
              education loan was approved after your I-20 was issued, simply explain:
            </P>
            <Strong>
              &quot;At the time of I-20 issuance, we showed available family funds. Since then, our
              formal education loan has been sanctioned for additional support.&quot;
            </Strong>
            <P>
              <span className="mt-3 block text-foreground/70">
                Avoid adding random, unexplained third-party sponsors last minute.
              </span>
            </P>
          </>
        ),
      },
      {
        q: "Can I deposit last-minute funds into my bank account for the interview?",
        plain:
          "Avoid sudden, unexplained deposits. Large sums appearing in your sponsor's bank account right before your interview raise immediate suspicion. Consular officers look for consistent financial history. Sudden transfers signal borrowed money rather than genuine family support.",
        answer: (
          <P>
            Avoid sudden, unexplained deposits. Large sums appearing in your sponsor&rsquo;s bank
            account right before your interview raise immediate suspicion. Consular officers look
            for consistent financial history. Sudden transfers signal borrowed money rather than
            genuine family support.
          </P>
        ),
      },
    ],
  },
  {
    category: "Category D · DS-160 & Social Media Details",
    items: [
      {
        q: "What is the biggest mistake students make when filling out the DS-160?",
        plain:
          "Treating the DS-160 as a generic form instead of your main written evidence. The officer reads your DS-160 before asking you questions. If your spoken answers contradict details on your DS-160 (e.g., job titles, financial sponsors, or field of study), it raises immediate red flags. Consistency between your DS-160 and interview answers is everything.",
        answer: (
          <P>
            Treating the DS-160 as a generic form instead of your main written evidence. The officer
            reads your DS-160 before asking you questions. If your spoken answers contradict details
            on your DS-160 (e.g., job titles, financial sponsors, or field of study), it raises
            immediate red flags. Consistency between your DS-160 and interview answers is
            everything.
          </P>
        ),
      },
      {
        q: "How should I enter my LinkedIn and social media details on the DS-160?",
        plain:
          "Provide clean, searchable handles (e.g., linkedin.com/in/rahulsharma). Ensure your public display name and photo match your official DS-160 details. Inconsistent handles or unidentifiable profiles create unnecessary friction during background checks.",
        answer: (
          <P>
            Provide clean, searchable handles (e.g., linkedin.com/in/rahulsharma). Ensure your public
            display name and photo match your official DS-160 details. Inconsistent handles or
            unidentifiable profiles create unnecessary friction during background checks.
          </P>
        ),
      },
    ],
  },
];

export const REFUSAL_GROUPS: GuideGroup[] = [
  {
    category: "Category A · Re-application Strategy & Rejection Logic",
    items: [
      {
        q: "Why is a second F-1 interview much harder than the first?",
        plain:
          "On your second attempt, the officer starts with prior refusal notes on their screen. If you repeat the same DS-160, the same generic opening lines, and the same rehearsed answers, you are almost guaranteed a second refusal. You must establish a clear shift in presentation within the first 15–20 seconds.",
        answer: (
          <P>
            On your second attempt, the officer starts with prior refusal notes on their screen. If
            you repeat the same DS-160, the same generic opening lines, and the same rehearsed
            answers, you are almost guaranteed a second refusal. You must establish a clear shift in
            presentation within the first 15–20 seconds.
          </P>
        ),
      },
      {
        q: "What does a 214(b) refusal actually mean?",
        plain:
          "A 214(b) refusal means you failed to convince the consular officer of your non-immigrant intent or financial credibility during the short interview window. It is not a permanent ban—it simply means your presentation failed that day.",
        answer: (
          <P>
            A 214(b) refusal means you failed to convince the consular officer of your non-immigrant
            intent or financial credibility during the short interview window. It is not a permanent
            ban—it simply means your presentation failed that day.
          </P>
        ),
      },
    ],
  },
  {
    category: "Category B · Overcoming Profile Red Flags",
    items: [
      {
        q: 'How do I prove "Intent to Return" to India without sounding fake?',
        plain:
          'Saying "I love my country" or "My family lives in India" is a statement, not proof. Officers approve concrete industry logic. Generic pitch: "I want global exposure and then I will return to India." Strategic pitch: "I am specializing in Supply Chain Analytics to return and help solve the 15% operational loss in our family enterprise / target industry in India."',
        answer: (
          <>
            <P>
              Saying &quot;I love my country&quot; or &quot;My family lives in India&quot; is a
              statement, not proof. Officers approve concrete industry logic.
            </P>
            <Weak>&quot;I want global exposure and then I will return to India.&quot;</Weak>
            <Strong>
              &quot;I am specializing in Supply Chain Analytics to return and help solve the 15%
              operational loss in our family enterprise / target industry in India.&quot;
            </Strong>
          </>
        ),
      },
      {
        q: "How do I explain a Second Master's degree after getting refused?",
        plain:
          'Avoid saying "I just want to study more". Frame it as a necessary career progression: "My first degree provided general Computer Science foundations, but this specialized Master\'s fills a specific skill gap in Generative AI Ethics required for emerging tech regulatory roles in India."',
        answer: (
          <>
            <P>
              Avoid saying &quot;I just want to study more&quot;. Frame it as a necessary career
              progression:
            </P>
            <Strong>
              &quot;My first degree provided general Computer Science foundations, but this
              specialized Master&apos;s fills a specific skill gap in Generative AI Ethics required
              for emerging tech regulatory roles in India.&quot;
            </Strong>
          </>
        ),
      },
      {
        q: "What if I have a high current salary in India? Why does the officer doubt my intent?",
        plain:
          'If you earn a high salary, the officer will wonder: "Why spend ₹50L–₹1Cr if your career in India is already strong?" You must prove a clear career jump: "This specialized degree unlocks senior managerial/fintech leadership roles in India that require global financial modeling expertise."',
        answer: (
          <>
            <P>
              If you earn a high salary, the officer will wonder: &quot;Why spend ₹50L–₹1Cr if your
              career in India is already strong?&quot; You must prove a clear career jump:
            </P>
            <Strong>
              &quot;This specialized degree unlocks senior managerial/fintech leadership roles in
              India that require global financial modeling expertise.&quot;
            </Strong>
          </>
        ),
      },
      {
        q: "What if I applied to 10–12 universities and got rejected?",
        plain:
          'Applying everywhere without focus makes you look scattered. Avoid saying "I applied everywhere." Frame your choices intentionally: "I evaluated multiple options, but finalized the programs that directly align with my specialization and long-term career goals in India."',
        answer: (
          <>
            <P>
              Applying everywhere without focus makes you look scattered. Avoid saying &quot;I
              applied everywhere.&quot; Frame your choices intentionally:
            </P>
            <Strong>
              &quot;I evaluated multiple options, but finalized the programs that directly align with
              my specialization and long-term career goals in India.&quot;
            </Strong>
          </>
        ),
      },
    ],
  },
];

export const ALL_FAQS: { q: string; plain: string }[] = [...FRESHER_GROUPS, ...REFUSAL_GROUPS]
  .flatMap((g) => g.items)
  .map((i) => ({ q: i.q, plain: i.plain }));
